<template>
  <div
    v-if="!isPdf && components.length > 0"
    class="d-print-none d-none d-lg-block"
    :style="tooltipStyle"
  >
    <span
      :id="id"
      class="cursor-pointer"
      tabindex="0"
      role="button"
      aria-label="information"
    >
      <icon
        icon="info-circle"
        class="text-accent"
        aria-hidden="true"
      />
    </span>
    <StandardPopover
      :target="id"
      triggers="hover"
      placement="bottomleft"
    >
      <div class="text-left tooltip-body">
        <component
          :is="comp"
          v-for="(comp, idx) of components"
          :key="idx"
        />
      </div>
    </StandardPopover>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, CSSProperties } from 'vue';
import { uniqueId } from 'lodash';
import { useIsPdf } from '@/composables/usePdf';
import { registry } from '@/whitelabel/hsbc/components/disclaimer/registry';
import { notNull } from '@/utils/notnull';

const isPdf = useIsPdf();

export default defineComponent({
  name: 'FactsheetTooltip',
  props: {
    names: {
      type: [String, Array] as PropType<string | string[]>,
      required: true,
    },
    topOffset: {
      type: String,
      required: false,
    },
    rightOffset: {
      type: String,
      required: false,
      default: '50px',
    },
  },
  setup(props) {
    const id = uniqueId();

    const names = computed(() => (typeof props.names === 'string' ? [props.names] : props.names));

    const components = computed(() => {
      return names.value.map((name) => registry[name as keyof typeof registry]).filter(notNull);
    });

    const tooltipStyle = computed((): CSSProperties => {
      return {
        position: 'absolute',
        top: 0,
        right: 0,
        'margin-top': props.topOffset,
        'margin-right': props.rightOffset,
      };
    });

    return {
      id,
      isPdf,
      components,
      tooltipStyle,
    };
  },
});
</script>

<style scoped>
/*
 * Markdown compiles to <p>, which has a default bottom margin that shows up
 * as extra space under the popover text.
 */
.tooltip-body :deep(p) {
  margin-bottom: 0;
}
</style>
